import React from "react";
import PropTypes from "prop-types";
import Mail from "components/icons/Mail";
import CallIcon from "components/icons/Call";
import MapMarkerIcon from "components/icons/MapMarker";
import UserIcon from "components/icons/User";
import styled from "styled-components";
import ChatBubbleIcon from "components/icons/ChatBubble";
import getMapLink from "utils/getMapLink";
import cx from "classnames";
import { track } from "utils/tracking";
import trackingEvents from "utils/trackingEvents";
import { isValidTitle } from "utils/validateTitle";
import OpenNewTab from "components/icons/OpenNewTab";
import useBreakpoint from "utils/hooks/useBreakpoint";

const IconContainer = styled.div.attrs(({ white }) => ({
  className: cx(
    "flex justify-center items-center w-30-mattamy h-30-mattamy inline mr-2 rounded-full",
    {
      "bg-mattamy-blue text-light-blue": !white,
      "bg-white text-dark-gray-bg": white,
    }
  ),
}))`
  min-width: 1.875rem;
`;

const CallIconContainer = styled.div`
  min-width: 1.875rem;
`;

const SectionHeading = styled.h3.attrs({
  className: "font-trade-gothic-20 text-3xl mb-1 md:mb-3",
})``;

const Contact = ({
  white,
  title,
  email,
  firstAddress,
  secondAddress,
  city,
  zipcode,
  state,
  stateAbbreviation,
  phoneNumberCall,
  phoneNumberMessage,
  spacedIcons = false,
  personName,
  latitude,
  longitude,
  gmbLink = {},
  note,
}) => {
  const breakpoint = useBreakpoint(
    typeof window !== "undefined" && window.innerWidth
  );
  const isMobile = ["sm", "xs"].includes(breakpoint);

  return (
    <div
      className={cx("flex flex-col w-full", {
        "text-mattamy-blue": !white,
        "text-white": white,
      })}
    >
      {isValidTitle(title) && <SectionHeading>{title}</SectionHeading>}
      {isValidTitle(note) && (
        <div className="flex items-center mb-2">
          <span className="font-graphie text-mattamy-blue">{note}</span>
        </div>
      )}
      <div
        className={cx("flex flex-col h-full w-full font-graphie text-base", {
          "mt-2 md:mt-0": spacedIcons,
        })}
      >
        {personName && (
          <div className="mb-4 flex items-center break-all">
            <div className="flex justify-center items-center w-30-mattamy h-30-mattamy inline mr-2 rounded-full">
              <UserIcon className="h-4 w-4" />
            </div>
            <span>{personName}</span>
          </div>
        )}
        {!isMobile && email && (
          <div className="flex items-center break-all">
            <IconContainer aria-hidden="true" white={white} tabIndex={-1}>
              <Mail className="h-4 w-4" />
            </IconContainer>
            <a
              className="underline"
              href={`mailto:${email}`}
              onClick={() => {
                track({ eventId: trackingEvents.Email });
              }}
              aria-label={"Email to " + email}
            >
              {email}
            </a>
          </div>
        )}
        {!isMobile && firstAddress && (
          <div
            className={cx("flex", {
              "items-start": secondAddress || city || zipcode || state,
              "items-center": !secondAddress,
            })}
          >
            <IconContainer tabIndex={-1} aria-hidden="true" white={white}>
              <MapMarkerIcon className="h-4 w-4" />
            </IconContainer>
            <div
              className={cx("flex flex-col", {
                "pt-1": secondAddress,
              })}
            >
              <a
                className="underline"
                href={
                  gmbLink && gmbLink.href
                    ? gmbLink.href
                    : latitude && longitude
                    ? getMapLink(latitude + "," + longitude)
                    : getMapLink(
                        firstAddress +
                          " " +
                          secondAddress +
                          " " +
                          city +
                          " " +
                          state +
                          " " +
                          zipcode
                      )
                }
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  track({ eventId: trackingEvents.DrivingDirections });
                }}
                aria-label={
                  "Go to " +
                  firstAddress +
                  " " +
                  secondAddress +
                  " " +
                  city +
                  " " +
                  state +
                  " " +
                  zipcode +
                  ". Opens in Googlemaps"
                }
              >
                <p className=" flex flex-wrap">
                  {firstAddress}
                  {!secondAddress &&
                    !(city || zipcode || stateAbbreviation) && (
                      <OpenNewTab className="w-4 ml-2 " />
                    )}
                </p>

                {secondAddress && (
                  <p className=" flex flex-wrap">
                    {secondAddress}
                    {!(city || zipcode || stateAbbreviation) && (
                      <OpenNewTab className="w-4 ml-2 " />
                    )}
                  </p>
                )}
                {(city || zipcode || stateAbbreviation) && (
                  <p className="flex flex-wrap">
                    {city}, {stateAbbreviation} {zipcode}
                    {<OpenNewTab className="w-4 ml-2 " />}
                  </p>
                )}
              </a>
            </div>
          </div>
        )}
        {!isMobile && phoneNumberCall && (
          <div className="mt-4 flex items-center break-all">
            <CallIconContainer
              tabIndex={-1}
              aria-hidden="true"
              className="rounded-full  mr-2"
            >
              <CallIcon className="w-30-mattamy h-30-mattamy" />
            </CallIconContainer>
            <a
              className="underline"
              href={`tel:${phoneNumberCall}`}
              onClick={() => {
                track({ eventId: trackingEvents.ClickToCall });
              }}
              aria-label={"Call to " + phoneNumberCall}
            >
              {phoneNumberCall}
            </a>
          </div>
        )}
        {!isMobile && phoneNumberMessage && (
          <div className="mt-4 flex items-center break-all">
            <IconContainer tabIndex={-1} aria-hidden="true" white={white}>
              <ChatBubbleIcon className="h-4 w-4" />
            </IconContainer>
            <a
              className="underline"
              href={`sms:${phoneNumberMessage}`}
              onClick={() => {
                track({ eventId: trackingEvents.ClickToText });
              }}
              aria-label={"Text to " + phoneNumberMessage}
            >
              {phoneNumberMessage}
            </a>
          </div>
        )}

        {isMobile && (
          <div className="grid grid-cols-2 gap-4">
            {email && (
              <div className="flex bg-mattamy-blue text-white rounded py-4 justify-left">
                <div className="ml-4 flex justify-center bg-white text-mattamy-blue items-center w-30-mattamy h-30-mattamy inline mr-2 rounded-full">
                  <Mail className="h-4 w-4" />
                </div>
                <a
                  //className="underline"
                  href={`mailto:${email}`}
                  onClick={() => {
                    track({ eventId: trackingEvents.Email });
                  }}
                  aria-label={"Email to " + email}
                >
                  {"Email"}
                </a>
              </div>
            )}
            {firstAddress && (
              <div
                className={cx(
                  "flex bg-mattamy-blue text-white rounded py-4 justify-left",
                  {}
                )}
              >
                <div
                  tabIndex={-1}
                  aria-hidden="true"
                  //white={white}
                  className="rounded-full bg-white ml-4 mr-2"
                >
                  <MapMarkerIcon className="w-8 h-8 p-1 text-mattamy-blue " />
                </div>
                <div
                  className={cx("flex flex-col", {
                    "pt-1": secondAddress,
                  })}
                >
                  <a
                    //className="underline"
                    href={
                      gmbLink && gmbLink.href
                        ? gmbLink.href
                        : latitude && longitude
                        ? getMapLink(latitude + "," + longitude)
                        : getMapLink(
                            firstAddress +
                              " " +
                              secondAddress +
                              " " +
                              city +
                              " " +
                              state +
                              " " +
                              zipcode
                          )
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      track({ eventId: trackingEvents.DrivingDirections });
                    }}
                    aria-label={
                      "Go to " +
                      firstAddress +
                      " " +
                      secondAddress +
                      " " +
                      city +
                      " " +
                      state +
                      " " +
                      zipcode +
                      ". Opens in Googlemaps"
                    }
                  >
                    Directions
                  </a>
                </div>
              </div>
            )}
            {phoneNumberCall && (
              <div className=" flex items-center break-all bg-mattamy-blue text-white rounded py-4 justify-left">
                <CallIconContainer
                  tabIndex={-1}
                  aria-hidden="true"
                  className="rounded-full ml-4 mr-2"
                >
                  <CallIcon className="w-30-mattamy h-30-mattamy" />
                </CallIconContainer>
                <a
                  //className="underline"
                  href={`tel:${phoneNumberCall}`}
                  onClick={() => {
                    track({ eventId: trackingEvents.ClickToCall });
                  }}
                  aria-label={"Call to " + phoneNumberCall}
                >
                  {"Call"}
                </a>
              </div>
            )}
            {phoneNumberMessage && (
              <div className="flex items-center break-all ">
                <IconContainer tabIndex={-1} aria-hidden="true" white={white}>
                  <ChatBubbleIcon className="h-4 w-4" />
                </IconContainer>
                <a
                  className="underline"
                  href={`sms:${phoneNumberMessage}`}
                  onClick={() => {
                    track({ eventId: trackingEvents.ClickToText });
                  }}
                  aria-label={"Text to " + phoneNumberMessage}
                >
                  {phoneNumberMessage}
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

Contact.propTypes = {
  white: PropTypes.bool,
  title: PropTypes.string,
  associateName: PropTypes.string,
  email: PropTypes.string,
  firstAddress: PropTypes.string,
  secondAddress: PropTypes.string,
  city: PropTypes.string,
  zipcode: PropTypes.string,
  state: PropTypes.string,
  stateAbbreviation: PropTypes.string,
  phoneNumberCall: PropTypes.string,
  phoneNumberMessage: PropTypes.string,
};

export default Contact;
